import { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Megaphone, User, IdentificationBadge, PencilSimple, X, MagnifyingGlass } from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { value: "accounts",   label: "Accounts" },
  { value: "salesman",   label: "Salesman" },
  { value: "picker",     label: "Picker" },
  { value: "scanner",    label: "Scanner" },
  { value: "packer",     label: "Packer" },
  { value: "collection", label: "Collection" },
  { value: "admin",      label: "Admin" },
];

const SCOPE_STYLE = {
  all:  { label: "All users", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: Megaphone },
  role: { label: "Role",      cls: "bg-blue-100    text-blue-800    border-blue-200",    Icon: IdentificationBadge },
  user: { label: "User",      cls: "bg-violet-100  text-violet-800  border-violet-200",  Icon: User },
};

const fmtDT = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const EMPTY = { text: "", scope: "all", target: "" };

export default function AdminNotes() {
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Filters
  const [filterScope, setFilterScope] = useState("all");   // all | scope-filter-value
  const [filterRole, setFilterRole] = useState("");        // role name when filterScope='role'
  const [filterUser, setFilterUser] = useState("");        // user mobile when filterScope='user'
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await client.get("/admin/dashboard-notes");
      setNotes(data);
    } catch (e) { toast.error(apiError(e)); }
    try {
      const { data: u } = await client.get("/users");
      setUsers(u.filter(x => !(x.roles || []).includes("superuser")));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const nameByMobile = useMemo(
    () => Object.fromEntries(users.map(u => [u.mobile, u.name])),
    [users]
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return notes.filter(n => {
      if (filterScope !== "all") {
        if (n.scope !== filterScope) return false;
        if (filterScope === "role" && filterRole && n.target !== filterRole) return false;
        if (filterScope === "user" && filterUser && n.target !== filterUser) return false;
      }
      if (!s) return true;
      return (n.text || "").toLowerCase().includes(s)
        || (n.target || "").toLowerCase().includes(s)
        || (n.target_name || "").toLowerCase().includes(s);
    });
  }, [notes, filterScope, filterRole, filterUser, q]);

  const startEdit = (n) => {
    setEditingId(n.id);
    setForm({ text: n.text, scope: n.scope, target: n.target || "" });
    // Scroll compose panel into view on mobile
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.text.trim()) return toast.error("Note text is required");
    if (form.scope !== "all" && !form.target) return toast.error(`Select a ${form.scope}`);
    setSaving(true);
    try {
      if (editingId) {
        await client.patch(`/admin/dashboard-notes/${editingId}`, {
          text: form.text.trim(), scope: form.scope, target: form.scope === "all" ? "" : form.target,
        });
        toast.success("Note updated");
      } else {
        await client.post("/admin/dashboard-notes", {
          text: form.text.trim(), scope: form.scope, target: form.scope === "all" ? "" : form.target,
        });
        toast.success("Note published");
      }
      setForm(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this note?")) return;
    try {
      await client.delete(`/admin/dashboard-notes/${id}`);
      if (editingId === id) cancelEdit();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filterActive = filterScope !== "all" || filterRole || filterUser || q;

  return (
    <div>
      <PageHeader
        title="Admin Notes"
        subtitle="Announcements shown on the dashboard of every user, a role, or a single user"
      />
      <Section>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Compose / Edit */}
          <div className="lg:col-span-1 bg-white border border-slate-200 rounded-sm p-5 h-fit sticky top-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-cap">{editingId ? "Edit Note" : "Compose Note"}</div>
              {editingId && (
                <button onClick={cancelEdit} data-testid="admin-notes-cancel-edit" className="text-xs font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1">
                  <X size={12}/> Cancel edit
                </button>
              )}
            </div>

            <div className="mb-3">
              <label className="label-cap">Visible to</label>
              <Select value={form.scope} onValueChange={(v)=>setForm({...form, scope: v, target: ""})}>
                <SelectTrigger data-testid="admin-notes-scope" className="rounded-sm mt-1">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  <SelectItem value="role">Specific role</SelectItem>
                  <SelectItem value="user">Specific user</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.scope === "role" && (
              <div className="mb-3">
                <label className="label-cap">Role</label>
                <Select value={form.target} onValueChange={(v)=>setForm({...form, target: v})}>
                  <SelectTrigger data-testid="admin-notes-role" className="rounded-sm mt-1"><SelectValue placeholder="Select role"/></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.scope === "user" && (
              <div className="mb-3">
                <label className="label-cap">User</label>
                <Select value={form.target} onValueChange={(v)=>setForm({...form, target: v})}>
                  <SelectTrigger data-testid="admin-notes-user" className="rounded-sm mt-1"><SelectValue placeholder="Select user"/></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.mobile}>{u.name} · {u.mobile}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="mb-3">
              <label className="label-cap">Note</label>
              <Textarea data-testid="admin-notes-text" value={form.text} onChange={(e)=>setForm({...form, text: e.target.value})} rows={5} placeholder="Write your announcement…" className="rounded-sm mt-1"/>
            </div>

            <Button
              data-testid="admin-notes-save"
              onClick={save}
              disabled={saving}
              className={`w-full ${editingId ? "bg-blue-600 hover:bg-blue-700" : "bg-orange-600 hover:bg-orange-700"} text-white rounded-sm`}>
              {editingId ? <PencilSimple size={14} className="mr-1"/> : <Plus size={14} className="mr-1"/>}
              {saving ? (editingId ? "Updating…" : "Publishing…") : (editingId ? "Save changes" : "Publish note")}
            </Button>
          </div>

          {/* List + Filters */}
          <div className="lg:col-span-2">
            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 space-y-3" data-testid="admin-notes-filters">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mr-1">Filter by</div>
                {[
                  {v:"all",  l:"All"},
                  {v:"all",  l:"Broadcast", key:"broadcast"},
                  {v:"role", l:"Role"},
                  {v:"user", l:"User"},
                ].map((p,idx) => {
                  // Special: "broadcast" is scope=all with no target
                  const active = p.key === "broadcast"
                    ? filterScope === "all" && !filterRole && !filterUser
                    : filterScope === p.v && idx === 0 ? true : filterScope === p.v;
                  const isBroadcast = p.key === "broadcast";
                  const isAll       = idx === 0;
                  return (
                    <button
                      key={`${p.v}-${idx}`}
                      data-testid={`admin-notes-filter-${p.key || p.v}`}
                      onClick={()=>{
                        if (isAll)              { setFilterScope("all"); setFilterRole(""); setFilterUser(""); }
                        else if (isBroadcast)   { setFilterScope("all"); setFilterRole(""); setFilterUser(""); }
                        else                    { setFilterScope(p.v); setFilterRole(""); setFilterUser(""); }
                      }}
                      className={`text-xs px-3 py-1.5 rounded-sm border font-semibold ${
                        (isAll && filterScope === "all") || (!isAll && filterScope === p.v)
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                      {p.l}
                    </button>
                  );
                })}
                {filterScope === "role" && (
                  <Select value={filterRole} onValueChange={setFilterRole}>
                    <SelectTrigger data-testid="admin-notes-filter-role-select" className="rounded-sm h-8 text-xs w-48"><SelectValue placeholder="Any role"/></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {filterScope === "user" && (
                  <Select value={filterUser} onValueChange={setFilterUser}>
                    <SelectTrigger data-testid="admin-notes-filter-user-select" className="rounded-sm h-8 text-xs w-56"><SelectValue placeholder="Any user"/></SelectTrigger>
                    <SelectContent>
                      {users.map(u => <SelectItem key={u.id} value={u.mobile}>{u.name} · {u.mobile}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {filterActive && (
                  <button onClick={()=>{setFilterScope("all");setFilterRole("");setFilterUser("");setQ("");}} data-testid="admin-notes-filter-clear" className="text-xs px-2 py-1.5 rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold flex items-center gap-1"><X size={10}/> Clear</button>
                )}
                <div className="ml-auto text-xs text-slate-500">{filtered.length} of {notes.length}</div>
              </div>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
                <Input data-testid="admin-notes-search" placeholder="Search text, role, user…" value={q} onChange={(e)=>setQ(e.target.value)} className="rounded-sm pl-8 h-9"/>
              </div>
            </div>

            {/* List */}
            <div className="space-y-3" data-testid="admin-notes-list">
              {filtered.length === 0 && (
                <div className="text-sm text-slate-500 bg-white border border-dashed border-slate-300 rounded-sm p-6 text-center">
                  {notes.length === 0
                    ? "No notes yet. Publish one from the left to make it visible on user dashboards."
                    : "No notes match this filter."}
                </div>
              )}
              {filtered.map(n => {
                const s = SCOPE_STYLE[n.scope] || SCOPE_STYLE.all;
                const I = s.Icon;
                const isEditing = editingId === n.id;
                const targetLabel = n.scope === "user"
                  ? (n.target_name || nameByMobile[n.target] || n.target)
                  : n.target;
                return (
                  <div
                    key={n.id}
                    data-testid={`admin-note-${n.id}`}
                    onClick={()=>startEdit(n)}
                    className={`bg-white border rounded-sm p-4 cursor-pointer transition-shadow ${isEditing ? "border-blue-400 ring-1 ring-blue-200 shadow" : "border-slate-200 hover:shadow-sm"}`}>
                    <div className="flex items-start gap-3">
                      <div className={`h-9 w-9 rounded-sm grid place-items-center border ${s.cls}`}>
                        <I size={16} weight="bold"/>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${s.cls}`}>{s.label}</span>
                          {n.scope !== "all" && (
                            <span className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded-sm border border-slate-200">
                              {targetLabel}
                            </span>
                          )}
                          {isEditing && (
                            <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-sm">Editing…</span>
                          )}
                          <span className="ml-auto text-xs text-slate-400">{fmtDT(n.updated_at)}</span>
                        </div>
                        <div className="mt-2 text-sm text-slate-800 whitespace-pre-line">{n.text}</div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          data-testid={`admin-note-edit-${n.id}`}
                          onClick={(e)=>{e.stopPropagation(); startEdit(n);}}
                          className="p-1.5 rounded-sm text-blue-600 hover:bg-blue-50" title="Edit">
                          <PencilSimple size={14}/>
                        </button>
                        <button
                          data-testid={`admin-note-delete-${n.id}`}
                          onClick={(e)=>{e.stopPropagation(); remove(n.id);}}
                          className="p-1.5 rounded-sm text-rose-600 hover:bg-rose-50" title="Delete">
                          <Trash size={14}/>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
